import React, { createContext, useContext } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';

export interface VesselPendingPurchaseOrderRow {
  id: number;
  orderDate?: string | null;
  ref?: string | null;
  caseId: number;
  supplierId: number;
  quotationId: number;
  pendingLineCount: number;
  totalPendingQty: number;
  lineItemCount: number;
  supplyCase?: {
    id: number;
    ref?: string | null;
    vesselId?: number | null;
    caseDate?: string | null;
    eta?: string | null;
    vessel?: { id: number; vesselName?: string | null; vesselAbrv?: string | null } | null;
    port?: { name: string; code?: string | null } | null;
  } | null;
  quotation?: { id: number; ref?: string | null; quoDate?: string | null } | null;
  supplier?: { id: number; name: string; code?: string | null; email?: string | null; isActive?: boolean } | null;
}

export interface VesselPendingPurchaseOrderLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  orderedQty: number;
  deliveredQty: number;
  pendingQty: number;
  itemNotes?: string | null;
  item?: { level1?: string | null; level2?: string | null; level3?: string | null } | null;
  unit?: { id?: number; ucode?: string | null; descr?: string | null } | null;
}

export interface VesselPendingPurchaseOrderDetail {
  id: number;
  orderDate?: string | null;
  ref?: string | null;
  caseId: number;
  quotationId: number;
  supplyCase?: VesselPendingPurchaseOrderRow['supplyCase'];
  quotation?: VesselPendingPurchaseOrderRow['quotation'];
  supplier?: VesselPendingPurchaseOrderRow['supplier'];
  lineItems: VesselPendingPurchaseOrderLine[];
}

const VesselPendingDeliveriesListContext = createContext<number | null>(null);

export function VesselPendingDeliveriesListProvider(props: { vesselId: number; children: React.ReactNode }) {
  return React.createElement(VesselPendingDeliveriesListContext.Provider, { value: props.vesselId }, props.children);
}

function buildPendingForVesselListQuery(
  vesselId: number,
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
): string {
  const rest = buildListQuery(searchParams, page, limit, options);
  const params = new URLSearchParams(rest.startsWith('?') ? rest.slice(1) : rest);
  params.set('vesselId', String(vesselId));
  return `?${params.toString()}`;
}

export function useVesselPendingDeliveriesListData(
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  const vesselId = useContext(VesselPendingDeliveriesListContext);
  const q =
    vesselId != null && Number.isFinite(vesselId)
      ? buildPendingForVesselListQuery(vesselId, searchParams, page, limit, options)
      : null;
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<VesselPendingPurchaseOrderRow> | VesselPendingPurchaseOrderRow[]
  >(q ? `/supply-purchase-orders/pending-for-vessel${q}` : null, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<VesselPendingPurchaseOrderRow>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
}

export function useVesselPendingPurchaseOrderDetail(purchaseOrderId: number | undefined, vesselId: number | undefined) {
  const key =
    purchaseOrderId != null &&
    vesselId != null &&
    Number.isFinite(purchaseOrderId) &&
    Number.isFinite(vesselId)
      ? `/supply-purchase-orders/pending-for-vessel/${purchaseOrderId}?vesselId=${vesselId}`
      : null;
  const { data, error, isLoading, mutate } = useSWR<VesselPendingPurchaseOrderDetail>(key, fetcher, {
    revalidateOnFocus: true,
  });
  return { data, item: data, isLoading, isError: error, mutate };
}
